import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Edit2, Trash2, Plus, Settings, Download, Send } from 'lucide-react';
import { suppliersApi, tasksApi, brandsApi, skuMappingApi } from '../services/api';

const SuppliersPage = () => {
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [configuringSupplier, setConfiguringSupplier] = useState(null);
  // Маппинг артикулов
  const [mappingData, setMappingData] = useState(null);
  const [mappingLoading, setMappingLoading] = useState(false);
  const [mappingUploading, setMappingUploading] = useState(false);
  const [mappingSearchQuery, setMappingSearchQuery] = useState('');
  const [mappingUploadResult, setMappingUploadResult] = useState(null);
  const [testingCloudUrl, setTestingCloudUrl] = useState(false);
  const [cloudTestResult, setCloudTestResult] = useState(null);
  const [configFormData, setConfigFormData] = useState({
    // Excel fields
    file_url: '',
    file_url_2: '',
    column_sku: 'B',
    column_brand: 'B',
    column_stock: 'C',
    xls_price_column_index: null,
    header_row: 8,
    reverse_sku_parts: false,
    strip_price_code: false,
    digits_only_sku: false,
    xls_sku_mask: '',
    xls_brand_mask: '',
    // Auth fields
    auth_enabled: false,
    auth_username: '',
    auth_password: '',
    auth_type: 'form',
    auth_login_url: '',
    auth_username_field: '',
    auth_password_field: '',
    // FTP fields
    ftp_host: '',
    ftp_port: 21,
    ftp_path: '',
    ftp_file_pattern: '',
    ftp_passive: true,
    // Cloud fields
    cloud_url: '',
    cloud_file_regex: '',
    // Simple XML fields
    xml_url: '',
    xml_tag_sku: 'Артикул',
    xml_tag_brand: 'Бренд',
    xml_tag_stock: 'Количество',
    xml_tag_price: 'РРЦ',
    xml_tag_currency: '',
    xml_reverse_sku_parts: false,
    xml_strip_price_code: false,
    xml_digits_only_sku: false,
    xml_ignore_brand: false,
    xml_sku_mask: '',
    xml_brand_mask: '',
    // XML 1C fields
    xml_1c_enabled: false,
    xml_import_url: '',
    xml_offers_url: '',
    xml_offers_price_tag: null,
    xml_offers_currency_tag: null,
    brand_property_guid: '',
    name_property_name: 'Полное наименование',
    // XML 1C v3 fields (CommerceML 3.1)
    xml_1c_v3_enabled: false,
    // XML 1C v2 fields (CommerceML 2.x)
    xml_1c_v2_enabled: false,
  });
  const [formData, setFormData] = useState({
    name: '',
    update_method: 'xls',
    description: '',
    is_active: true,
    debug_mode: false,
    update_prices: false,
    update_prices_zero_stock: false,
    self_upload: false,
    update_interval_days: '',
    email: '',
    brand_ids: [],
    comment: '',
    delivery_days: [],
    order_time: '',
    delivery_day_count_min: '',
    delivery_day_count_max: '',
  });
  const [brandSearchQuery, setBrandSearchQuery] = useState('');
  const [isDeliveryDaysOpen, setIsDeliveryDaysOpen] = useState(false);

  const { data: suppliers, isLoading } = useQuery({
    queryKey: ['suppliers'],
    queryFn: async () => {
      const res = await suppliersApi.getAll();
      return res.data;
    },
  });

  const { data: allBrands } = useQuery({
    queryKey: ['brands'],
    queryFn: async () => {
      const res = await brandsApi.getAll({ limit: 1000 });
      return res.data;
    },
    staleTime: 5 * 60 * 1000,
  });

  const createMutation = useMutation({
    mutationFn: (data) => suppliersApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      toast.success('Поставщик создан');
      closeModal();
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Ошибка создания');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => suppliersApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      toast.success('Поставщик обновлен');
      closeModal();
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Ошибка обновления');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => suppliersApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      toast.success('Поставщик удален');
    },
    onError: () => toast.error('Ошибка удаления'),
  });

  const saveConfigMutation = useMutation({
    mutationFn: ({ id, data }) => suppliersApi.saveConfig(id, data),
    onSuccess: () => {
      toast.success('Конфигурация сохранена');
      setIsConfigModalOpen(false);
      setConfiguringSupplier(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Ошибка сохранения');
    },
  });

  const downloadAndProcessMutation = useMutation({
    mutationFn: (id) => suppliersApi.downloadAndProcess(id),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      toast.success(res.data.message || 'Файл скачан и добавлен в очередь');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Ошибка скачивания файла');
    },
  });

  const clearStocksMutation = useMutation({
    mutationFn: (id) => suppliersApi.clearStocks(id),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      toast.success(res.data.message || 'Остатки очищены');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Ошибка очистки остатков');
    },
  });

  const clearPricesMutation = useMutation({
    mutationFn: (id) => suppliersApi.clearPrices(id),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      toast.success(res.data.message || 'Цены очищены');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Ошибка очистки цен');
    },
  });

  const sendSupplierRemainsMutation = useMutation({
    mutationFn: (id) => tasksApi.sendRemainsBySupplier(id),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      toast.success(res.data.message || 'Остатки поставщика отправлены');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Ошибка отправки остатков');
    },
  });

  const sendSupplierPricesMutation = useMutation({
    mutationFn: (id) => tasksApi.sendPricesBySupplier(id),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      toast.success(res.data.message || 'Цены поставщика отправлены');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Ошибка отправки цен');
    },
  });

  const handleTestCloudUrl = async () => {
    if (!configFormData.cloud_url) {
      toast.error('Укажите URL облака');
      return;
    }

    setTestingCloudUrl(true);
    setCloudTestResult(null);

    try {
      const response = await suppliersApi.testCloudUrl({
        cloud_url: configFormData.cloud_url,
        cloud_file_regex: configFormData.cloud_file_regex || '.*'
      });

      setCloudTestResult({
        success: true,
        files: response.data.files || [],
        matched_file: response.data.matched_file,
        total_files: response.data.total_files
      });

      if (response.data.matched_file) {
        toast.success(`Найден файл: ${response.data.matched_file.name}`);
      } else if (response.data.total_files > 0) {
        toast.success(`Найдено ${response.data.total_files} файлов`);
      } else {
        toast.error('Файлы не найдены');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Ошибка при тестировании URL';
      toast.error(errorMsg);
      setCloudTestResult({
        success: false,
        error: errorMsg
      });
    } finally {
      setTestingCloudUrl(false);
    }
  };

  const openCreateModal = () => {
    setEditingSupplier(null);
    setFormData({
      name: '',
      update_method: 'xls',
      description: '',
      is_active: true,
      debug_mode: false,
      update_prices: false,
      update_prices_zero_stock: false,
      self_upload: false,
      update_interval_days: '',
      email: '',
      login: '',
      password: '',
      brand_ids: [],
      comment: '',
      delivery_days: [],
      order_time: '',
      delivery_day_count_min: '',
      delivery_day_count_max: '',
    });
    setBrandSearchQuery('');
    setIsDeliveryDaysOpen(false);
    setIsModalOpen(true);
  };

  const openEditModal = (supplier) => {
    setEditingSupplier(supplier);
    setFormData({
      name: supplier.name,
      update_method: supplier.update_method,
      description: supplier.description || '',
      is_active: supplier.is_active,
      debug_mode: supplier.debug_mode || false,
      update_prices: supplier.update_prices || false,
      update_prices_zero_stock: supplier.update_prices_zero_stock || false,
      self_upload: supplier.self_upload || false,
      update_interval_days: supplier.update_interval_days || '',
      email: supplier.email || '',
      login: supplier.login || '',
      password: '',
      brand_ids: supplier.brand_ids || [],
      comment: supplier.comment || '',
      delivery_days: supplier.delivery_days || [],
      order_time: supplier.order_time || '',
      delivery_day_count_min: supplier.delivery_day_count_min ?? '',
      delivery_day_count_max: supplier.delivery_day_count_max ?? '',
    });
    setBrandSearchQuery('');
    setIsDeliveryDaysOpen(false);
    setMappingSearchQuery('');
    // Загружаем маппинг артикулов
    setMappingLoading(true);
    setMappingData(null);
    skuMappingApi.getAll(supplier.id)
      .then(res => setMappingData(res.data))
      .catch(() => setMappingData({ items: [], total: 0 }))
      .finally(() => setMappingLoading(false));
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingSupplier(null);
    setMappingData(null);
    setMappingSearchQuery('');
    setMappingUploadResult(null);
  };

  const openConfigModal = async (supplier) => {
    setConfiguringSupplier(supplier);
    try {
      const res = await suppliersApi.getConfig(supplier.id);
      const mappings = res.data.config_data?.xml_1c_mappings || {};
      setConfigFormData({
        // Excel fields
        file_url: res.data.file_url || '',
        file_url_2: res.data.file_url_2 || '',
        column_sku: res.data.column_sku || 'B',
        column_brand: res.data.column_brand || 'B',
        column_stock: res.data.column_stock || 'C',
        xls_price_column_index: res.data.xls_price_column_index || null,
        header_row: res.data.header_row || 8,
        reverse_sku_parts: res.data.reverse_sku_parts || false,
        strip_price_code: res.data.strip_price_code || false,
        digits_only_sku: res.data.digits_only_sku || false,
        xls_ignore_brand: res.data.xls_ignore_brand || false,
        xls_sku_mask: res.data.xls_sku_mask || '',
        xls_brand_mask: res.data.xls_brand_mask || '',
        // Auth fields
        auth_enabled: res.data.auth_enabled || false,
        auth_username: res.data.auth_username || '',
        auth_password: res.data.auth_password || '',
        auth_type: res.data.auth_type || 'form',
        auth_login_url: res.data.auth_login_url || '',
        auth_username_field: res.data.auth_username_field || '',
        auth_password_field: res.data.auth_password_field || '',
        // FTP fields
        ftp_host: res.data.ftp_host || '',
        ftp_port: res.data.ftp_port || 21,
        ftp_path: res.data.ftp_path || '',
        ftp_file_pattern: res.data.ftp_file_pattern || '',
        ftp_passive: res.data.ftp_passive !== undefined ? res.data.ftp_passive : true,
        // Cloud fields
        cloud_url: res.data.cloud_url || '',
        cloud_file_regex: res.data.cloud_file_regex || '',
        // Simple XML fields
        xml_url: res.data.xml_url || '',
        xml_tag_sku: res.data.xml_tag_sku || 'Артикул',
        xml_tag_brand: res.data.xml_tag_brand || 'Бренд',
        xml_tag_stock: res.data.xml_tag_stock || 'Количество',
        xml_tag_price: res.data.xml_tag_price || 'РРЦ',
        xml_tag_currency: res.data.xml_tag_currency || '',
        xml_reverse_sku_parts: res.data.xml_reverse_sku_parts || false,
        xml_strip_price_code: res.data.xml_strip_price_code || false,
        xml_digits_only_sku: res.data.xml_digits_only_sku || false,
        xml_ignore_brand: res.data.xml_ignore_brand || false,
        xml_sku_mask: res.data.xml_sku_mask || '',
        xml_brand_mask: res.data.xml_brand_mask || '',
        // XML 1C fields
        xml_1c_enabled: res.data.xml_1c_enabled || false,
        xml_import_url: res.data.xml_import_url || '',
        xml_offers_url: res.data.xml_offers_url || '',
        xml_offers_price_tag: res.data.xml_offers_price_tag || null,
        xml_offers_currency_tag: res.data.xml_offers_currency_tag || null,
        brand_property_guid: mappings.brand_property_guid || '',
        name_property_name: mappings.name_property_name || 'Полное наименование',
        xml_1c_v3_enabled: res.data.xml_1c_v3_enabled || false,
        xml_1c_v2_enabled: res.data.xml_1c_v2_enabled || false,
      });
    } catch (error) {
      // Если конфигурации нет, используем дефолтные значения
      setConfigFormData({
        file_url: '',
        file_url_2: '',
        column_brand: 'B',
        column_stock: 'C',
        xls_price_column_index: null,
        header_row: 8,
        reverse_sku_parts: false,
        strip_price_code: false,
        digits_only_sku: false,
        xls_ignore_brand: false,
        xls_sku_mask: '',
        xls_brand_mask: '',
        auth_enabled: false,
        auth_username: '',
        auth_password: '',
        auth_type: 'form',
        auth_login_url: '',
        auth_username_field: '',
        auth_password_field: '',
        ftp_host: '',
        ftp_port: 21,
        ftp_path: '',
        ftp_file_pattern: '',
        ftp_passive: true,
        cloud_url: '',
        cloud_file_regex: '',
        xml_url: '',
        xml_tag_sku: 'Артикул',
        xml_tag_brand: 'Бренд',
        xml_tag_stock: 'Количество',
        xml_tag_price: 'РРЦ',
        xml_tag_currency: '',
        xml_reverse_sku_parts: false,
        xml_strip_price_code: false,
        xml_digits_only_sku: false,
        xml_ignore_brand: false,
        xml_sku_mask: '',
        xml_brand_mask: '',
        xml_1c_enabled: false,
        xml_import_url: '',
        xml_offers_url: '',
        xml_offers_price_tag: null,
        xml_offers_currency_tag: null,
        brand_property_guid: '',
        name_property_name: 'Полное наименование',
        xml_1c_v3_enabled: false,
        xml_1c_v2_enabled: false,
      });
    }
    setIsConfigModalOpen(true);
  };

  const closeConfigModal = () => {
    setIsConfigModalOpen(false);
    setConfiguringSupplier(null);
    setCloudTestResult(null);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      if (mappingUploadResult) { setMappingUploadResult(null); return; }
      if (isConfigModalOpen) { closeConfigModal(); return; }
      if (isModalOpen) { closeModal(); return; }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isModalOpen, isConfigModalOpen, mappingUploadResult]);

  const handleConfigSubmit = (e) => {
    e.preventDefault();
    
    // Подготовка данных в зависимости от метода обновления
    const configData = {
      file_url: configFormData.file_url,
      file_url_2: configFormData.file_url_2 || null,
      column_sku: configFormData.column_sku,
      column_brand: configFormData.column_brand,
      column_stock: configFormData.column_stock,
      xls_price_column_index: configFormData.xls_price_column_index,
      header_row: configFormData.header_row,
      reverse_sku_parts: configFormData.reverse_sku_parts,
      strip_price_code: configFormData.strip_price_code,
      digits_only_sku: configFormData.digits_only_sku || false,
      xls_ignore_brand: configFormData.xls_ignore_brand || false,
      xls_sku_mask: configFormData.xls_sku_mask || null,
      xls_brand_mask: configFormData.xls_brand_mask || null,
      // Добавляем поля авторизации
      auth_enabled: configFormData.auth_enabled,
      auth_username: configFormData.auth_username,
      auth_password: configFormData.auth_password,
      auth_type: configFormData.auth_type,
      auth_login_url: configFormData.auth_login_url,
      auth_username_field: configFormData.auth_username_field,
      auth_password_field: configFormData.auth_password_field,
      // Добавляем FTP поля
      ftp_host: configFormData.ftp_host,
      ftp_port: configFormData.ftp_port,
      ftp_path: configFormData.ftp_path,
      ftp_file_pattern: configFormData.ftp_file_pattern,
      ftp_passive: configFormData.ftp_passive,
    };

    // Добавляем облако поля
    if (configuringSupplier.update_method === 'cloud') {
      configData.cloud_url = configFormData.cloud_url;
      configData.cloud_file_regex = configFormData.cloud_file_regex;
    }

    // XLS-поля — для xls, ftp и cloud (у всех методов с файлами Excel)
    if (['xls', 'ftp', 'cloud'].includes(configuringSupplier.update_method)) {
      configData.header_row = configFormData.header_row ? parseInt(configFormData.header_row) : null;
    }

    // Добавляем простой XML поля
    if (configuringSupplier.update_method === 'xml') {
      configData.xml_url = configFormData.xml_url;
      configData.xml_tag_sku = configFormData.xml_tag_sku;
      configData.xml_tag_brand = configFormData.xml_tag_brand;
      configData.xml_tag_stock = configFormData.xml_tag_stock;
      configData.xml_tag_price = configFormData.xml_tag_price;
      configData.xml_tag_currency = configFormData.xml_tag_currency || null;
      configData.xml_reverse_sku_parts = configFormData.xml_reverse_sku_parts;
      configData.xml_strip_price_code = configFormData.xml_strip_price_code;
      configData.xml_digits_only_sku = configFormData.xml_digits_only_sku;
      configData.xml_ignore_brand = configFormData.xml_ignore_brand;
      configData.xml_sku_mask = configFormData.xml_sku_mask;
      configData.xml_brand_mask = configFormData.xml_brand_mask;
    }

    // Добавляем XML 1C поля если это XML метод
    if (configuringSupplier.update_method === 'xml_1c') {
      configData.xml_1c_enabled = configFormData.xml_1c_enabled;
      configData.xml_import_url = configFormData.xml_import_url;
      configData.xml_offers_url = configFormData.xml_offers_url;
      configData.xml_offers_price_tag = configFormData.xml_offers_price_tag;
      configData.xml_offers_currency_tag = configFormData.xml_offers_currency_tag || null;
      configData.config_data = {
        xml_1c_mappings: {
          brand_property_guid: configFormData.brand_property_guid,
          name_property_name: configFormData.name_property_name,
        }
      };
    }

    // Добавляем XML 1C v2 поля (CommerceML 2.x)
    if (configuringSupplier.update_method === 'xml_1c_v2') {
      configData.xml_1c_v2_enabled = configFormData.xml_1c_v2_enabled;
      configData.xml_offers_price_tag = configFormData.xml_offers_price_tag || null;
      configData.xml_offers_currency_tag = configFormData.xml_offers_currency_tag || null;
    }

    // Добавляем XML 1C v3 поля (CommerceML 3.1)
    if (configuringSupplier.update_method === 'xml_1c_v3') {
      configData.xml_1c_v3_enabled = configFormData.xml_1c_v3_enabled;
      configData.xml_offers_price_tag = configFormData.xml_offers_price_tag || null;
      configData.xml_offers_currency_tag = configFormData.xml_offers_currency_tag || null;
      configData.config_data = {
        xml_1c_mappings: {
          name_property_name: configFormData.name_property_name,
        }
      };
    }

    saveConfigMutation.mutate({ 
      id: configuringSupplier.id, 
      data: configData 
    });
  };

  const saveAndOpenConfig = async () => {
    // Сохраняем изменения поставщика перед открытием окна конфига
    if (editingSupplier) {
      const dataToSend = {
        ...formData,
        update_interval_days: formData.update_interval_days || 3,
        brand_ids: formData.brand_ids || [],
        delivery_day_count_min: formData.delivery_day_count_min !== '' ? parseInt(formData.delivery_day_count_min, 10) : null,
        delivery_day_count_max: formData.delivery_day_count_max !== '' ? parseInt(formData.delivery_day_count_max, 10) : null,
      };
      try {
        await suppliersApi.update(editingSupplier.id, dataToSend);
        queryClient.invalidateQueries({ queryKey: ['suppliers'] });
        toast.success('Поставщик сохранён');
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Ошибка сохранения поставщика');
        return; // Не открываем конфиг если сохранение не удалось
      }
    }
    closeModal();
    openConfigModal(editingSupplier);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const dataToSend = {
      ...formData,
      update_interval_days: formData.update_interval_days || 3,
      brand_ids: formData.brand_ids || [],
      delivery_day_count_min: formData.delivery_day_count_min !== '' ? parseInt(formData.delivery_day_count_min, 10) : null,
      delivery_day_count_max: formData.delivery_day_count_max !== '' ? parseInt(formData.delivery_day_count_max, 10) : null,
    };

    if (editingSupplier) {
      updateMutation.mutate({ id: editingSupplier.id, data: dataToSend });
    } else {
      createMutation.mutate(dataToSend);
    }
  };

  if (isLoading) {
    return (
      <div className="card">
        <div style={{ textAlign: 'center', padding: '40px' }}>Загрузка...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="card">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '4px' }}>Поставщики</h1>
            <div style={{ fontSize: '14px', color: '#6b7280' }}>
              Всего: <span style={{ fontWeight: '600', color: '#374151' }}>{suppliers?.length || 0}</span>
            </div>
          </div>
          <button
            className="btn btn-primary flex items-center gap-2"
            onClick={openCreateModal}
          >
            <Plus size={16} />
            Добавить поставщика
          </button>
        </div>

        <table className="table">
          <thead>
            <tr>
              <th>Название</th>
              <th>Метод обновления</th>
              <th>Бренды</th>
              <th>В наличии</th>
              <th>Цены</th>
              <th>Статус</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {suppliers && suppliers.map((supplier) => (
              <tr 
                key={supplier.id}
                onClick={() => openEditModal(supplier)}
                style={{ cursor: 'pointer' }}
              >
                <td style={{ fontWeight: '500' }}>{supplier.name}</td>
                <td>
                  <span style={{
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: '500',
                    textTransform: 'uppercase',
                    background: supplier.update_method === 'xls' ? '#dbeafe' : 
                               supplier.update_method === 'cloud' ? '#fef3c7' :
                               supplier.update_method === 'xml' ? '#d1fae5' :
                               supplier.update_method === 'xml_1c' ? '#ddd6fe' :
                               supplier.update_method === 'xml_1c_v3' ? '#fce7f3' :
                               supplier.update_method === 'xml_1c_v2' ? '#ecfdf5' : '#fef3c7',
                    color: supplier.update_method === 'xls' ? '#1e40af' : 
                           supplier.update_method === 'cloud' ? '#92400e' :
                           supplier.update_method === 'xml' ? '#065f46' :
                           supplier.update_method === 'xml_1c' ? '#5b21b6' :
                           supplier.update_method === 'xml_1c_v3' ? '#9d174d' :
                           supplier.update_method === 'xml_1c_v2' ? '#065f46' : '#92400e'
                  }}>
                    {supplier.update_method}
                  </span>
                </td>
                <td>
                  <span style={{ fontSize: '13px', color: supplier.brand_ids?.length ? '#374151' : '#9ca3af' }}>
                    {supplier.brand_ids?.length ? `${supplier.brand_ids.length} бр.` : '—'}
                  </span>
                </td>
                <td>
                  <span style={{
                    fontWeight: '500',
                    color: (supplier.in_stock_products || 0) > 0 ? '#059669' : '#6b7280'
                  }}>
                    {supplier.in_stock_products || 0}
                  </span>
                </td>
                <td>
                  <span style={{
                    fontWeight: '500',
                    color: (supplier.prices_count || 0) > 0 ? '#ea580c' : '#6b7280'
                  }}>
                    {supplier.prices_count || 0}
                  </span>
                </td>
                <td>
                  <span style={{
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: '500',
                    background: supplier.is_active ? '#d1fae5' : '#fee2e2',
                    color: supplier.is_active ? '#065f46' : '#991b1b'
                  }}>
                    {supplier.is_active ? 'Активен' : 'Неактивен'}
                  </span>
                </td>
                <td>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      className="btn"
                      style={{ 
                        padding: '6px 12px',
                        background: '#3b82f6',
                        color: 'white',
                        fontSize: '12px'
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        downloadAndProcessMutation.mutate(supplier.id);
                      }}
                      disabled={downloadAndProcessMutation.isPending}
                      title="Скачать файл по URL и обработать"
                    >
                      <Download size={16} />
                    </button>
                    <button
                      className="btn"
                      style={{ 
                        padding: '6px 12px',
                        background: '#10b981',
                        color: 'white',
                        fontSize: '12px'
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm(`Отправить остатки поставщика "${supplier.name}" на сайт?`)) {
                          sendSupplierRemainsMutation.mutate(supplier.id);
                        }
                      }}
                      disabled={sendSupplierRemainsMutation.isPending}
                      title="Отправить остатки поставщика на сайт"
                    >
                      <Send size={16} />
                    </button>
                    <button
                      className="btn"
                      style={{ 
                        padding: '6px 12px',
                        background: '#f59e0b',
                        color: 'white',
                        fontSize: '12px'
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm(`Отправить цены поставщика "${supplier.name}" на сайт?`)) {
                          sendSupplierPricesMutation.mutate(supplier.id);
                        }
                      }}
                      disabled={sendSupplierPricesMutation.isPending}
                      title="Отправить цены поставщика на сайт"
                    >
                      💰
                    </button>
                    <button
                      className="btn btn-primary"
                      style={{ padding: '6px 12px' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        openEditModal(supplier);
                      }}
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      className="btn btn-danger"
                      style={{ padding: '6px 12px' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm(`Удалить поставщика "${supplier.name}"?`)) {
                          deleteMutation.mutate(supplier.id);
                        }
                      }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {!suppliers || suppliers.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
            Поставщики не найдены. Создайте первого поставщика.
          </div>
        ) : null}
      </div>

      {/* Модальное окно */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h2>{editingSupplier ? 'Редактировать поставщика' : 'Новый поставщик'}</h2>
              <button className="modal-close" onClick={closeModal}>×</button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                <div className="form-group">
                  <label>Название *</label>
                  <input
                    type="text"
                    className="input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Метод обновления</label>
                  <select
                    className="input"
                    value={formData.update_method}
                    onChange={(e) => setFormData({ ...formData, update_method: e.target.value })}
                    required
                  >
                    <option value="xls">XLS (Excel файлы)</option>
                    <option value="cloud">Облако (Яндекс Диск и т.д.)</option>
                    <option value="xml">XML (Простой XML)</option>
                    <option value="xml_1c">XML 1C (Пара import.xml + offers.xml)</option>
                    <option value="xml_1c_v3">XML 1C v3 (CommerceML 3.1, множество файлов)</option>
                    <option value="xml_1c_v2">XML 1C v2 (CommerceML 2.x, import___ / rests___ / prices___)</option>
                  </select>
                </div>

                {/* Чекбокс "Загружаем сами" для XLS */}
                {formData.update_method === 'xls' && (
                  <div className="form-group" style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontWeight: '500' }}>
                      <input
                        type="checkbox"
                        checked={formData.self_upload || false}
                        onChange={e => setFormData({ ...formData, self_upload: e.target.checked })}
                        style={{ marginRight: '8px' }}
                      />
                      Загружаем сами
                    </label>
                    <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px', display: 'block', marginLeft: '24px' }}>
                      Если включено, файл будет загружаться вручную через страницу "Загрузка"
                    </small>
                  </div>
                )}

                {/* Кнопка настроек для Excel */}
                {editingSupplier && formData.update_method === 'xls' && (
                  <div className="form-group">
                    <button
                      type="button"
                      className="btn"
                      style={{ 
                        width: '100%',
                        padding: '10px',
                        background: '#8b5cf6',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px'
                      }}
                      onClick={saveAndOpenConfig}
                    >
                      <Settings size={18} />
                      Настроить колонки и URL файла Excel
                    </button>
                  </div>
                )}

                {/* Кнопка настроек для облака */}
                {editingSupplier && formData.update_method === 'cloud' && (
                  <div className="form-group">
                    <button
                      type="button"
                      className="btn"
                      style={{ 
                        width: '100%',
                        padding: '10px',
                        background: '#f59e0b',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px'
                      }}
                      onClick={saveAndOpenConfig}
                    >
                      <Settings size={18} />
                      Настроить облако (URL и regex)
                    </button>
                  </div>
                )}

                {/* Кнопка настроек для простого XML */}
                {editingSupplier && formData.update_method === 'xml' && (
                  <div className="form-group">
                    <button
                      type="button"
                      className="btn"
                      style={{ 
                        width: '100%',
                        padding: '10px',
                        background: '#10b981',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px'
                      }}
                      onClick={saveAndOpenConfig}
                    >
                      <Settings size={18} />
                      Настроить теги и URL XML файла
                    </button>
                  </div>
                )}

                {/* Кнопка настроек для XML 1C */}
                {editingSupplier && formData.update_method === 'xml_1c' && (
                  <div className="form-group">
                    <button
                      type="button"
                      className="btn"
                      style={{ 
                        width: '100%',
                        padding: '10px',
                        background: '#3b82f6',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px'
                      }}
                      onClick={saveAndOpenConfig}
                    >
                      <Settings size={18} />
                      Настроить XML 1C (URL файлов и маппинги)
                    </button>
                  </div>
                )}

                {/* Логин и пароль для xml_1c_v3 */}
                {formData.update_method === 'xml_1c_v3' && (
                  <>
                    <div className="form-group">
                      <label>Логин 1С (для авторизации выгрузки)</label>
                      <input
                        type="text"
                        className="input"
                        value={formData.login}
                        onChange={(e) => setFormData({ ...formData, login: e.target.value })}
                        placeholder="opus"
                        autoComplete="off"
                      />
                    </div>
                    <div className="form-group">
                      <label>Пароль 1С{editingSupplier ? ' (оставьте пустым, чтобы не менять)' : ''}</label>
                      <input
                        type="password"
                        className="input"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        placeholder={editingSupplier ? (editingSupplier.has_password ? '•••••• (задан)' : 'Пароль не задан') : 'Введите пароль'}
                        autoComplete="new-password"
                      />
                      {editingSupplier && editingSupplier.has_password && !formData.password && (
                        <small style={{ color: '#16a34a', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                          ✅ Пароль установлен. Введите новый чтобы изменить.
                        </small>
                      )}
                    </div>
                  </>
                )}

                {/* Логин и пароль для xml_1c_v2 */}
                {formData.update_method === 'xml_1c_v2' && (
                  <>
                    <div className="form-group">
                      <label>Логин 1С (для авторизации выгрузки)</label>
                      <input
                        type="text"
                        className="input"
                        value={formData.login}
                        onChange={(e) => setFormData({ ...formData, login: e.target.value })}
                        placeholder="login"
                        autoComplete="off"
                      />
                    </div>
                    <div className="form-group">
                      <label>Пароль 1С{editingSupplier ? ' (оставьте пустым, чтобы не менять)' : ''}</label>
                      <input
                        type="password"
                        className="input"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        placeholder={editingSupplier ? (editingSupplier.has_password ? '•••••• (задан)' : 'Пароль не задан') : 'Введите пароль'}
                        autoComplete="new-password"
                      />
                      {editingSupplier && editingSupplier.has_password && !formData.password && (
                        <small style={{ color: '#16a34a', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                          ✅ Пароль установлен. Введите новый чтобы изменить.
                        </small>
                      )}
                    </div>
                  </>
                )}

                {/* Кнопка настроек для XML 1C v3 */}
                {editingSupplier && formData.update_method === 'xml_1c_v3' && (
                  <div className="form-group">
                    <button
                      type="button"
                      className="btn"
                      style={{ 
                        width: '100%',
                        padding: '10px',
                        background: '#db2777',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px'
                      }}
                      onClick={saveAndOpenConfig}
                    >
                      <Settings size={18} />
                      Настроить XML 1C v3 (CommerceML 3.1)
                    </button>
                  </div>
                )}

                {/* Кнопка настроек для XML 1C v2 */}
                {editingSupplier && formData.update_method === 'xml_1c_v2' && (
                  <div className="form-group">
                    <button
                      type="button"
                      className="btn"
                      style={{ 
                        width: '100%',
                        padding: '10px',
                        background: '#16a34a',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px'
                      }}
                      onClick={saveAndOpenConfig}
                    >
                      <Settings size={18} />
                      Настроить XML 1C v2 (CommerceML 2.x)
                    </button>
                  </div>
                )}

                {/* Кнопка настроек для нового поставщика */}
                {!editingSupplier && formData.update_method === 'xls' && (
                  <div className="form-group">
                    <div style={{ 
                      padding: '12px',
                      background: '#fef3c7',
                      border: '1px solid #fbbf24',
                      borderRadius: '6px',
                      fontSize: '14px',
                      color: '#78350f'
                    }}>
                      💡 После создания поставщика, нажмите на иконку шестеренки в таблице, чтобы настроить колонки Excel и указать URL файла для автоматического скачивания.
                    </div>
                  </div>
                )}

                <div className="form-group">
                  <label>Описание</label>
                  <textarea
                    className="input"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    placeholder="Дополнительная информация о поставщике"
                  />
                </div>

                <div className="form-group">
                  <label>Комментарий</label>
                  <textarea
                    className="input"
                    value={formData.comment}
                    onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                    rows={2}
                    placeholder="Внутренний комментарий"
                  />
                </div>

                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      style={{ marginRight: '8px' }}
                    />
                    Активен
                  </label>
                </div>

                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={formData.debug_mode}
                      onChange={(e) => setFormData({ ...formData, debug_mode: e.target.checked })}
                      style={{ marginRight: '8px' }}
                    />
                    Режим отладки (показывать ненайденные товары)
                  </label>
                </div>

                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={formData.update_prices}
                      onChange={(e) => setFormData({ ...formData, update_prices: e.target.checked })}
                      style={{ marginRight: '8px' }}
                    />
                    Обновлять цены (сохранять цены из файла)
                  </label>
                </div>

                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={formData.update_prices_zero_stock}
                      onChange={(e) => setFormData({ ...formData, update_prices_zero_stock: e.target.checked })}
                      style={{ marginRight: '8px' }}
                    />
                    Обновлять цены нулевых товаров (нет в наличии)
                  </label>
                </div>

                <div className="form-group">
                  <label>Срок обновления (дней)</label>
                  <input
                    type="number"
                    className="input"
                    min="1"
                    placeholder="3"
                    value={formData.update_interval_days || ''}
                    onChange={(e) => setFormData({ ...formData, update_interval_days: e.target.value ? parseInt(e.target.value) : null })}
                    style={{ width: '160px' }}
                  />
                  <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                    Используется для уведомлений о просроченном обновлении
                  </small>
                </div>

                <div className="form-group">
                  <label>Email поставщика</label>
                  <input
                    type="email"
                    className="input"
                    placeholder="supplier@example.com"
                    value={formData.email || ''}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                  <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                    Письма с вложениями от этого адреса будут автоматически обработаны
                  </small>
                </div>

                {/* Дни доставки */}
                <div className="form-group">
                  <button
                    type="button"
                    onClick={() => setIsDeliveryDaysOpen(v => !v)}
                    style={{
                      padding: '8px 14px',
                      background: (formData.delivery_days?.length > 0 || formData.delivery_day_count_min !== '' || formData.delivery_day_count_max !== '') ? '#2563eb' : '#f3f4f6',
                      color: (formData.delivery_days?.length > 0 || formData.delivery_day_count_min !== '' || formData.delivery_day_count_max !== '') ? '#fff' : '#374151',
                      border: '1px solid ' + ((formData.delivery_days?.length > 0 || formData.delivery_day_count_min !== '' || formData.delivery_day_count_max !== '') ? '#2563eb' : '#d1d5db'),
                      borderRadius: '6px',
                      fontSize: '14px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}
                  >
                    📅 Дни доставки
                    {formData.delivery_days?.length > 0 && (
                      <span style={{
                        background: 'rgba(255,255,255,0.3)',
                        borderRadius: '10px',
                        padding: '1px 7px',
                        fontSize: '12px',
                      }}>
                        {formData.delivery_days.length}
                      </span>
                    )}
                    {(formData.delivery_day_count_min !== '' || formData.delivery_day_count_max !== '') && (
                      <span style={{
                        background: 'rgba(255,255,255,0.3)',
                        borderRadius: '10px',
                        padding: '1px 7px',
                        fontSize: '12px',
                      }}>
                        {formData.delivery_day_count_min || '?'}–{formData.delivery_day_count_max || '?'} дн.
                      </span>
                    )}
                    <span style={{ marginLeft: 'auto', fontSize: '12px' }}>{isDeliveryDaysOpen ? '▲' : '▼'}</span>
                  </button>

                  {isDeliveryDaysOpen && (
                    <div style={{
                      marginTop: '10px',
                      padding: '14px',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      background: '#f9fafb',
                    }}>
                      <div style={{ marginBottom: '12px' }}>
                        <div style={{ fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                          Дни недели
                        </div>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          {[
                            { num: 1, label: 'Пн' },
                            { num: 2, label: 'Вт' },
                            { num: 3, label: 'Ср' },
                            { num: 4, label: 'Чт' },
                            { num: 5, label: 'Пт' },
                            { num: 6, label: 'Сб' },
                            { num: 7, label: 'Вс' },
                          ].map(({ num, label }) => {
                            const isChecked = (formData.delivery_days || []).includes(num);
                            return (
                              <label key={num} style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '4px',
                                cursor: 'pointer',
                                padding: '6px 10px',
                                borderRadius: '6px',
                                border: '1px solid ' + (isChecked ? '#2563eb' : '#d1d5db'),
                                background: isChecked ? '#eff6ff' : '#fff',
                                minWidth: '40px',
                                fontSize: '13px',
                                fontWeight: isChecked ? '600' : '400',
                                color: isChecked ? '#1d4ed8' : '#374151',
                              }}>
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={(e) => {
                                    const days = formData.delivery_days || [];
                                    setFormData({
                                      ...formData,
                                      delivery_days: e.target.checked
                                        ? [...days, num].sort((a, b) => a - b)
                                        : days.filter(d => d !== num),
                                    });
                                  }}
                                  style={{ display: 'none' }}
                                />
                                {label}
                              </label>
                            );
                          })}
                        </div>
                      </div>
                      <div style={{ marginBottom: '12px' }}>
                        <div style={{ fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                          Срок доставки (дней)
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <input
                            type="number"
                            min="0"
                            placeholder="от"
                            className="input"
                            value={formData.delivery_day_count_min}
                            onChange={(e) => setFormData({ ...formData, delivery_day_count_min: e.target.value })}
                            style={{ width: '80px' }}
                          />
                          <span style={{ color: '#6b7280', fontSize: '13px' }}>—</span>
                          <input
                            type="number"
                            min="0"
                            placeholder="до"
                            className="input"
                            value={formData.delivery_day_count_max}
                            onChange={(e) => setFormData({ ...formData, delivery_day_count_max: e.target.value })}
                            style={{ width: '80px' }}
                          />
                          <span style={{ color: '#6b7280', fontSize: '13px' }}>дн.</span>
                        </div>
                      </div>
                      <div>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '6px' }}>
                          Время заказа
                        </label>
                        <input
                          type="time"
                          className="input"
                          value={formData.order_time || ''}
                          onChange={(e) => setFormData({ ...formData, order_time: e.target.value })}
                          style={{ width: '140px' }}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {editingSupplier?.last_stock_sync && (
                  <div className="form-group">
                    <label>Последнее обновление остатков</label>
                    <div style={{
                      padding: '8px 12px', background: '#f0fdf4', border: '1px solid #bbf7d0',
                      borderRadius: '6px', fontSize: '14px', color: '#166534', display: 'inline-block'
                    }}>
                      {new Date(editingSupplier.last_stock_sync).toLocaleString('ru-RU')}
                    </div>
                    {editingSupplier.update_interval_days && (() => {
                      const daysSince = Math.floor((Date.now() - new Date(editingSupplier.last_stock_sync)) / 86400000);
                      const overdue = daysSince > editingSupplier.update_interval_days;
                      return (
                        <small style={{ color: overdue ? '#dc2626' : '#6b7280', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                          {overdue
                            ? `⚠️ Просрочено на ${daysSince - editingSupplier.update_interval_days} дн. (обновлялось ${daysSince} дн. назад)`
                            : `Обновлялось ${daysSince} дн. назад`}
                        </small>
                      );
                    })()}
                  </div>
                )}

                {/* Бренды поставщика */}
                <div className="form-group">
                  <label style={{ fontWeight: '600', marginBottom: '8px', display: 'block' }}>
                    Бренды поставщика
                    {formData.brand_ids?.length > 0 && (
                      <span style={{ marginLeft: '8px', fontSize: '12px', color: '#6b7280', fontWeight: '400' }}>
                        выбрано: {formData.brand_ids.length}
                      </span>
                    )}
                  </label>
                  <small style={{ color: '#6b7280', fontSize: '12px', marginBottom: '8px', display: 'block' }}>
                    Используется при настройке «Не использовать бренд из файла» — поиск ведётся только среди этих брендов
                  </small>
                  <input
                    type="text"
                    className="input"
                    placeholder="Поиск бренда..."
                    value={brandSearchQuery}
                    onChange={(e) => setBrandSearchQuery(e.target.value)}
                    style={{ marginBottom: '8px' }}
                  />
                  <div style={{
                    maxHeight: '200px',
                    overflowY: 'auto',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    padding: '4px'
                  }}>
                    {(allBrands || [])
                      .filter(b => !brandSearchQuery || b.name.toLowerCase().includes(brandSearchQuery.toLowerCase()))
                      .sort((a, b) => {
                        const aChecked = formData.brand_ids?.includes(a.id) ? 1 : 0;
                        const bChecked = formData.brand_ids?.includes(b.id) ? 1 : 0;
                        if (bChecked !== aChecked) return bChecked - aChecked;
                        return a.name.localeCompare(b.name, 'ru');
                      })
                      .map(brand => (
                        <label key={brand.id} style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '6px 8px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          backgroundColor: formData.brand_ids?.includes(brand.id) ? '#eff6ff' : 'transparent',
                        }}>
                          <input
                            type="checkbox"
                            checked={formData.brand_ids?.includes(brand.id) || false}
                            onChange={(e) => {
                              const ids = formData.brand_ids || [];
                              setFormData({
                                ...formData,
                                brand_ids: e.target.checked
                                  ? [...ids, brand.id]
                                  : ids.filter(id => id !== brand.id)
                              });
                            }}
                          />
                          <span style={{ fontSize: '14px' }}>{brand.name}</span>
                        </label>
                      ))
                    }
                    {(allBrands || []).filter(b => !brandSearchQuery || b.name.toLowerCase().includes(brandSearchQuery.toLowerCase())).length === 0 && (
                      <div style={{ padding: '12px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>Бренды не найдены</div>
                    )}
                  </div>
                  {formData.brand_ids?.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, brand_ids: [] })}
                      style={{ marginTop: '6px', background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: '13px' }}
                    >
                      Снять все
                    </button>
                  )}
                </div>

                {/* Маппинг артикулов поставщика */}
                {editingSupplier && (
                  <div className="form-group" style={{ marginTop: '20px', borderTop: '1px solid #e5e7eb', paddingTop: '20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <label style={{ fontWeight: '600', margin: 0 }}>
                        Маппинг артикулов
                        {mappingData?.total > 0 && (
                          <span style={{ marginLeft: '8px', fontSize: '12px', color: '#6b7280', fontWeight: '400' }}>
                            {mappingData.total} записей
                          </span>
                        )}
                      </label>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <label style={{
                          padding: '6px 12px', background: '#2563eb', color: 'white',
                          borderRadius: '6px', fontSize: '13px', cursor: 'pointer',
                          display: 'flex', alignItems: 'center', gap: '4px',
                          opacity: mappingUploading ? 0.6 : 1,
                        }}>
                          {mappingUploading ? 'Загрузка...' : 'Загрузить XLS'}
                          <input
                            type="file"
                            accept=".xls,.xlsx"
                            style={{ display: 'none' }}
                            disabled={mappingUploading}
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              setMappingUploading(true);
                              const fd = new FormData();
                              fd.append('file', file);
                              try {
                                const res = await skuMappingApi.uploadXls(editingSupplier.id, fd);
                                const d = res.data;
                                setMappingUploadResult(d);
                                const fresh = await skuMappingApi.getAll(editingSupplier.id);
                                setMappingData(fresh.data);
                              } catch (err) {
                                toast.error(err.response?.data?.detail || 'Ошибка загрузки');
                              } finally {
                                setMappingUploading(false);
                                e.target.value = '';
                              }
                            }}
                          />
                        </label>
                        {mappingData?.total > 0 && (
                          <button
                            type="button"
                            style={{ padding: '6px 12px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '6px', fontSize: '13px', cursor: 'pointer' }}
                            onClick={async () => {
                              if (!window.confirm('Очистить весь маппинг артикулов?')) return;
                              try {
                                await skuMappingApi.clearAll(editingSupplier.id);
                                setMappingData({ items: [], total: 0 });
                                toast.success('Маппинг очищен');
                              } catch { toast.error('Ошибка очистки'); }
                            }}
                          >
                            Очистить
                          </button>
                        )}
                      </div>
                    </div>
                    <small style={{ color: '#6b7280', fontSize: '12px', marginBottom: '8px', display: 'block' }}>
                      XLS-файл: колонка A — наш артикул (поиск по нормализованному), колонка B — артикул поставщика, колонка C — бренд (опционально, сужает поиск). Маппинг имеет наивысший приоритет.
                    </small>

                    {mappingLoading && <div style={{ color: '#6b7280', fontSize: '13px' }}>Загрузка...</div>}

                    {!mappingLoading && mappingData?.total > 0 && (
                      <>
                        <input
                          type="text"
                          className="input"
                          placeholder="Поиск по артикулу..."
                          value={mappingSearchQuery}
                          onChange={(e) => setMappingSearchQuery(e.target.value)}
                          style={{ marginBottom: '8px' }}
                        />
                        <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '6px' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                            <thead>
                              <tr style={{ background: '#f9fafb', position: 'sticky', top: 0 }}>
                                <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Наш артикул</th>
                                <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Артикул поставщика</th>
                                <th style={{ width: '36px' }}></th>
                              </tr>
                            </thead>
                            <tbody>
                              {(mappingData.items || []).filter(row =>
                                !mappingSearchQuery ||
                                row.product_sku?.toLowerCase().includes(mappingSearchQuery.toLowerCase()) ||
                                row.supplier_sku?.toLowerCase().includes(mappingSearchQuery.toLowerCase())
                              ).map(row => (
                                <tr key={row.id} style={{ borderTop: '1px solid #f3f4f6' }}>
                                  <td style={{ padding: '5px 10px', color: '#111827' }}>{row.product_sku}</td>
                                  <td style={{ padding: '5px 10px', color: '#4b5563' }}>{row.supplier_sku}</td>
                                  <td style={{ padding: '5px 6px', textAlign: 'center' }}>
                                    <button
                                      type="button"
                                      onClick={async () => {
                                        try {
                                          await skuMappingApi.deleteRow(editingSupplier.id, row.id);
                                          setMappingData(prev => ({
                                            ...prev,
                                            total: prev.total - 1,
                                            items: prev.items.filter(r => r.id !== row.id),
                                          }));
                                        } catch { toast.error('Ошибка удаления'); }
                                      }}
                                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: '16px', lineHeight: 1 }}
                                      title="Удалить"
                                    >×</button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </>
                    )}

                    {!mappingLoading && mappingData?.total === 0 && (
                      <div style={{ padding: '12px', textAlign: 'center', color: '#9ca3af', fontSize: '13px', border: '1px dashed #e5e7eb', borderRadius: '6px' }}>
                        Маппинг пуст. Загрузите XLS-файл с соответствиями артикулов.
                      </div>
                    )}
                  </div>
                )}

              </div>{/* end modal-body */}

              <div className="modal-footer">
                <div style={{ display: 'flex', gap: '8px', flex: 1 }}>
                  {editingSupplier && (
                    <>
                      <button 
                        type="button"
                        className="btn"
                        style={{ 
                          background: '#ef4444',
                          color: 'white'
                        }}
                        onClick={() => {
                          if (window.confirm(`Очистить все остатки поставщика "${editingSupplier.name}"? Это действие нельзя отменить.`)) {
                            clearStocksMutation.mutate(editingSupplier.id);
                          }
                        }}
                        disabled={clearStocksMutation.isPending}
                      >
                        Очистить остатки
                      </button>
                      <button 
                        type="button"
                        className="btn"
                        style={{ 
                          background: '#f97316',
                          color: 'white'
                        }}
                        onClick={() => {
                          if (window.confirm(`Очистить все цены поставщика "${editingSupplier.name}"? Это действие нельзя отменить.`)) {
                            clearPricesMutation.mutate(editingSupplier.id);
                          }
                        }}
                        disabled={clearPricesMutation.isPending}
                      >
                        Очистить цены
                      </button>
                    </>
                  )}
                </div>
                <button type="button" className="btn btn-secondary" onClick={closeModal}>
                  Отмена
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingSupplier ? 'Сохранить' : 'Создать'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Модальное окно настройки конфигурации */}
      {isConfigModalOpen && configuringSupplier && (
        <div className="modal-overlay" onClick={closeConfigModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h2>Настройка конфигурации: {configuringSupplier.name}</h2>
              <button className="modal-close" onClick={closeConfigModal}>×</button>
            </div>

            <form onSubmit={handleConfigSubmit}>
              <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                {/* Конфигурация для облака */}
                {configuringSupplier.update_method === 'cloud' && (
                  <>
                    <div style={{ 
                      background: '#fef3c7', 
                      padding: '12px', 
                      borderRadius: '6px',
                      marginBottom: '20px',
                      fontSize: '14px',
                      color: '#78350f',
                      borderLeft: '4px solid #f59e0b'
                    }}>
                      <strong>Облако:</strong> Укажите сокращенную ссылку на облако (Яндекс Диск) и regex паттерн для поиска файла.
                      Например, паттерн "Остатки.*МОСКВА" найдет файлы вроде "Остатки 10.02 МОСКВА.xls".
                    </div>

                    <div className="form-group">
                      <label>URL облака (сокращенная ссылка) *:</label>
                      <input
                        type="url"
                        className="input"
                        value={configFormData.cloud_url}
                        onChange={(e) => setConfigFormData({...configFormData, cloud_url: e.target.value})}
                        placeholder="https://clck.ru/3PFhHo"
                        required
                      />
                      <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                        Сокращенная ссылка на публичную папку Яндекс Диска
                      </small>
                    </div>

                    <div className="form-group">
                      <label>Regex паттерн для поиска файла *:</label>
                      <input
                        type="text"
                        className="input"
                        value={configFormData.cloud_file_regex}
                        onChange={(e) => setConfigFormData({...configFormData, cloud_file_regex: e.target.value})}
                        placeholder="Остатки.*МОСКВА"
                        required
                      />
                      <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                        Regex паттерн для поиска нужного файла (без учета регистра).
                        Примеры: "Остатки.*", ".*МОСКВА.*", "^Остатки.*\.xls$"
                      </small>
                    </div>

                    {/* Кнопка тестирования URL */}
                    <div style={{ marginBottom: '20px' }}>
                      <button
                        type="button"
                        className="btn"
                        onClick={handleTestCloudUrl}
                        disabled={testingCloudUrl || !configFormData.cloud_url}
                        style={{
                          background: testingCloudUrl ? '#9ca3af' : '#3b82f6',
                          color: 'white',
                          width: '100%',
                          padding: '10px',
                          fontWeight: '500'
                        }}
                      >
                        {testingCloudUrl ? '🔍 Тестирование...' : '🔍 Тест URL и паттерна'}
                      </button>
                    </div>

                    {/* Результаты тестирования */}
                    {cloudTestResult && (
                      <div style={{
                        padding: '16px',
                        borderRadius: '8px',
                        marginBottom: '20px',
                        background: cloudTestResult.success ? '#ecfdf5' : '#fef2f2',
                        border: `2px solid ${cloudTestResult.success ? '#10b981' : '#ef4444'}`
                      }}>
                        {cloudTestResult.success ? (
                          <>
                            <h4 style={{ 
                              color: '#065f46', 
                              marginBottom: '12px', 
                              fontSize: '14px',
                              fontWeight: '600'
                            }}>
                              ✅ Результаты теста
                              {cloudTestResult.cloud_type && (
                                <span style={{ marginLeft: '8px', fontWeight: '400', fontSize: '12px', color: '#047857' }}>
                                  ({cloudTestResult.cloud_type === 'nextcloud' ? '☁️ Nextcloud/ownCloud' : '☁️ Яндекс Диск'})
                                </span>
                              )}
                            </h4>
                            
                            {cloudTestResult.matched_file && (
                              <div style={{
                                background: '#d1fae5',
                                padding: '12px',
                                borderRadius: '6px',
                                marginBottom: '12px',
                                border: '1px solid #10b981'
                              }}>
                                <div style={{ fontSize: '13px', fontWeight: '600', color: '#065f46', marginBottom: '4px' }}>
                                  🎯 Найден подходящий файл:
                                </div>
                                <div style={{ fontSize: '14px', color: '#047857', fontWeight: '500' }}>
                                  {cloudTestResult.matched_file.name}
                                </div>
                                {cloudTestResult.matched_file.size && (
                                  <div style={{ fontSize: '12px', color: '#059669', marginTop: '4px' }}>
                                    Размер: {(cloudTestResult.matched_file.size / 1024).toFixed(2)} KB
                                  </div>
                                )}
                              </div>
                            )}

                            <div style={{ fontSize: '13px', color: '#065f46', marginBottom: '8px' }}>
                              Всего файлов в папке: <strong>{cloudTestResult.total_files}</strong>
                            </div>

                            {cloudTestResult.files && cloudTestResult.files.length > 0 && (
                              <details style={{ marginTop: '12px' }}>
                                <summary style={{ 
                                  cursor: 'pointer', 
                                  fontSize: '13px', 
                                  color: '#047857',
                                  fontWeight: '500',
                                  marginBottom: '8px'
                                }}>
                                  📁 Показать все файлы ({cloudTestResult.files.length})
                                </summary>
                                <div style={{
                                  background: 'white',
                                  padding: '8px',
                                  borderRadius: '4px',
                                  maxHeight: '200px',
                                  overflowY: 'auto',
                                  fontSize: '12px',
                                  marginTop: '8px'
                                }}>
                                  {cloudTestResult.files.map((file, index) => (
                                    <div 
                                      key={index}
                                      style={{
                                        padding: '6px 8px',
                                        borderBottom: index < cloudTestResult.files.length - 1 ? '1px solid #e5e7eb' : 'none',
                                        color: '#374151'
                                      }}
                                    >
                                      {file.name}
                                    </div>
                                  ))}
                                </div>
                              </details>
                            )}

                            {!cloudTestResult.matched_file && cloudTestResult.total_files > 0 && (
                              <div style={{ 
                                fontSize: '12px', 
                                color: '#d97706',
                                marginTop: '12px',
                                padding: '8px',
                                background: '#fef3c7',
                                borderRadius: '4px'
                              }}>
                                ⚠️ Файл, соответствующий паттерну, не найден. Проверьте regex.
                              </div>
                            )}
                          </>
                        ) : (
                          <>
                            <h4 style={{ 
                              color: '#991b1b', 
                              marginBottom: '8px', 
                              fontSize: '14px',
                              fontWeight: '600'
                            }}>
                              ❌ Ошибка тестирования
                            </h4>
                            <div style={{ fontSize: '13px', color: '#7f1d1d' }}>
                              {cloudTestResult.error}
                            </div>
                          </>
                        )}
                      </div>
                    )}

                    <div style={{ 
                      background: '#f9fafb', 
                      padding: '12px', 
                      borderRadius: '6px',
                      marginTop: '20px',
                      marginBottom: '12px'
                    }}>
                      <h3 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>Настройка колонок Excel</h3>
                      <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>
                        Укажите колонки для нужных данных из скачиваемого файла Excel:
                      </p>
                    </div>

                    <div className="form-group">
                      <label>Колонка артикула:</label>
                      <input
                        type="text"
                        className="input"
                        value={configFormData.column_sku}
                        onChange={(e) => setConfigFormData({...configFormData, column_sku: e.target.value})}
                        placeholder="B"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>Колонка бренда:</label>
                      <input
                        type="text"
                        className="input"
                        value={configFormData.column_brand}
                        onChange={(e) => setConfigFormData({...configFormData, column_brand: e.target.value})}
                        placeholder="B"
                      />
                    </div>

                    <div className="form-group">
                      <label>Колонка остатка:</label>
                      <input
                        type="text"
                        className="input"
                        value={configFormData.column_stock}
                        onChange={(e) => setConfigFormData({...configFormData, column_stock: e.target.value})}
                        placeholder="C"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>Индекс колонки цены (опционально):</label>
                      <input
                        type="number"
                        className="input"
                        value={configFormData.xls_price_column_index || ''}
                        onChange={(e) => setConfigFormData({...configFormData, xls_price_column_index: e.target.value ? parseInt(e.target.value) : null})}
                        placeholder="Например: 5"
                      />
                      <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px', display: 'block' }}>  
                        Используется только если включена галка "Обновлять цены". Индекс начинается с 0 (A=0, B=1, C=2...).
                      </small>
                    </div>

                    <div className="form-group">
                      <label>Строка с заголовками:</label>
                      <input
                        type="number"
                        className="input"
                        value={configFormData.header_row}
                        onChange={(e) => setConfigFormData({...configFormData, header_row: parseInt(e.target.value)})}
                        placeholder="1"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={configFormData.reverse_sku_parts || false}
                          onChange={(e) => setConfigFormData({...configFormData, reverse_sku_parts: e.target.checked})}
                          style={{ marginRight: '8px' }}
                        />
                        Реверсировать части артикула
                      </label>
                      <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                        Искать артикул в обоих вариантах: "21210OP" и "OP21210"
                      </small>
                    </div>

                    <div className="form-group">
                      <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={configFormData.strip_price_code || false}
                          onChange={(e) => setConfigFormData({...configFormData, strip_price_code: e.target.checked})}
                          style={{ marginRight: '8px' }}
                        />
                        Отбрасывать прайс-код
                      </label>
                      <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                        Убирать символы A-E в конце артикула через пробел: "1122 MK B" → "1122 MK"
                      </small>
                    </div>

                    <div className="form-group">
                      <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={configFormData.digits_only_sku || false}
                          onChange={(e) => setConfigFormData({...configFormData, digits_only_sku: e.target.checked})}
                          style={{ marginRight: '8px' }}
                        />
                        Отбрасывать буквы из артикула
                      </label>
                      <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                        Сравнивать только цифры: "500044" совпадёт с "KA500044". Работает если в артикуле не менее 5 цифр подряд.
                      </small>
                    </div>

                    <div className="form-group">
                      <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={configFormData.xls_ignore_brand || false}
                          onChange={(e) => setConfigFormData({...configFormData, xls_ignore_brand: e.target.checked})}
                          style={{ marginRight: '8px' }}
                        />
                        Не использовать бренд из файла
                      </label>
                      <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                        Искать только по артикулу среди брендов поставщика. Включить если в файле нет колонки бренда.
                      </small>
                    </div>

                    <div className="form-group">
                      <label>Маска артикула (regex):</label>
                      <input
                        type="text"
                        className="input"
                        value={configFormData.xls_sku_mask || ''}
                        onChange={(e) => setConfigFormData({...configFormData, xls_sku_mask: e.target.value})}
                        placeholder="Например: ^(\S+)\s"
                      />
                      <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                        Если артикул и бренд в одной колонке. Берётся группа 1. Строка пропускается если маска не совпала.<br/>
                        Пример: <code>^(\S+)\s</code> из "100/10049 Обои Cole &amp; Son" → "100/10049"
                      </small>
                    </div>

                    <div className="form-group">
                      <label>Маска бренда (regex):</label>
                      <input
                        type="text"
                        className="input"
                        value={configFormData.xls_brand_mask || ''}
                        onChange={(e) => setConfigFormData({...configFormData, xls_brand_mask: e.target.value})}
                        placeholder="Например: ^\S+\s+(.+)$"
                      />
                      <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                        Regex-маска для извлечения бренда из колонки. Берётся группа 1.<br/>
                        Пример: <code>^\S+\s+(.+)$</code> из "100/10049 Cole &amp; Son" → "Cole &amp; Son"
                      </small>
                    </div>
                  </>
                )}

                {/* Конфигурация для XML 1C */}
                {configuringSupplier.update_method === 'xml_1c' && (
                  <>
                    <div style={{ 
                      background: '#eff6ff', 
                      padding: '12px', 
                      borderRadius: '6px',
                      marginBottom: '20px',
                      fontSize: '14px',
                      color: '#1e40af',
                      borderLeft: '4px solid #3b82f6'
                    }}>
                      <strong>XML 1C:</strong> Укажите URL для автоматического скачивания import.xml и offers.xml.
                      Обновляются только остатки из файла offers.xml.
                    </div>

                    <div className="form-group">
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input
                          type="checkbox"
                          checked={configFormData.xml_1c_enabled}
                          onChange={(e) => setConfigFormData({...configFormData, xml_1c_enabled: e.target.checked})}
                          style={{ width: '18px', height: '18px' }}
                        />
                        <span>Включить импорт XML 1C</span>
                      </label>
                    </div>

                    {configFormData.xml_1c_enabled && (
                      <>
                        <div className="form-group">
                          <label>URL файла import.xml:</label>
                          <input
                            type="url"
                            className="input"
                            value={configFormData.xml_import_url}
                            onChange={(e) => setConfigFormData({...configFormData, xml_import_url: e.target.value})}
                            placeholder="https://example.com/import.xml"
                          />
                        </div>

                        <div className="form-group">
                          <label>URL файла offers.xml:</label>
                          <input
                            type="url"
                            className="input"
                            value={configFormData.xml_offers_url}
                            onChange={(e) => setConfigFormData({...configFormData, xml_offers_url: e.target.value})}
                            placeholder="https://example.com/offers.xml"
                          />
                        </div>

                        <div className="form-group">
                          <label>Тег цены в offers.xml (опционально):</label>
                          <input
                            type="text"
                            className="input"
                            value={configFormData.xml_offers_price_tag || ''}
                            onChange={(e) => setConfigFormData({...configFormData, xml_offers_price_tag: e.target.value || null})}
                            placeholder="Например: <Цены><Цена><ЦенаЗаЕдиницу>"
                          />
                          <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                            Используется только если включена галка "Обновлять цены". Укажите путь к тегу цены в offers.xml.
                          </small>
                        </div>

                        <div className="form-group">
                          <label>Тег валюты в offers.xml (опционально):</label>
                          <input
                            type="text"
                            className="input"
                            value={configFormData.xml_offers_currency_tag || ''}
                            onChange={(e) => setConfigFormData({...configFormData, xml_offers_currency_tag: e.target.value || null})}
                            placeholder="Например: Валюта"
                          />
                          <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                            Тег с кодом валюты (EUR, USD, GBP) в предложении offers.xml. Если не указан — цена в рублях.
                          </small>
                        </div>

                        <div style={{ 
                          background: '#f9fafb', 
                          padding: '12px', 
                          borderRadius: '6px',
                          marginTop: '20px',
                          marginBottom: '12px'
                        }}>
                          <h3 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>Маппинг данных</h3>
                        </div>

                        <div className="form-group">
                          <label>GUID свойства бренда в товаре:</label>
                          <input
                            type="text"
                            className="input"
                            value={configFormData.brand_property_guid}
                            onChange={(e) => setConfigFormData({...configFormData, brand_property_guid: e.target.value})}
                            placeholder="dee58fa5-a0f1-11e1-9460-000c292826f0"
                            required
                          />
                          <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                            ID свойства "Бренд" из раздела "Свойства" в XML
                          </small>
                        </div>

                        <div className="form-group">
                          <label>Название свойства для наименования:</label>
                          <input
                            type="text"
                            className="input"
                            value={configFormData.name_property_name}
                            onChange={(e) => setConfigFormData({...configFormData, name_property_name: e.target.value})}
                            placeholder="Полное наименование"
                            required
                          />
                          <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                            Название свойства из раздела "Наименования" (например, "Полное наименование")
                          </small>
                        </div>
                      </>
                    )}
                  </>
                )}

                {/* Конфигурация для XML 1C v3 (CommerceML 3.1) */}
                {configuringSupplier.update_method === 'xml_1c_v3' && (
                  <>
                    <div style={{ 
                      background: '#fdf2f8', 
                      padding: '12px', 
                      borderRadius: '6px',
                      marginBottom: '20px',
                      fontSize: '14px',
                      color: '#9d174d',
                      borderLeft: '4px solid #db2777'
                    }}>
                      <strong>XML 1C v3 (CommerceML 3.1):</strong> Файлы загружаются напрямую из 1С
                      через эндпоинт <code>/api/1c/v3/{'{'}supplier_id{'}'}/catalog</code>.
                      Поддерживаются файлы: <code>goods_*.xml</code>, <code>rests_*.xml</code>, <code>prices_*.xml</code>.
                      Обработка запускается по расписанию или вручную.
                    </div>

                    <div className="form-group">
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input
                          type="checkbox"
                          checked={configFormData.xml_1c_v3_enabled}
                          onChange={(e) => setConfigFormData({...configFormData, xml_1c_v3_enabled: e.target.checked})}
                          style={{ width: '18px', height: '18px' }}
                        />
                        <span>Включить импорт XML 1C v3</span>
                      </label>
                    </div>

                    {configFormData.xml_1c_v3_enabled && (
                      <>
                        <div style={{ 
                          background: '#f9fafb', 
                          padding: '12px', 
                          borderRadius: '6px',
                          marginBottom: '12px'
                        }}>
                          <h3 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '4px' }}>Эндпоинт приёма файлов из 1С</h3>
                          <code style={{ fontSize: '12px', color: '#374151', wordBreak: 'break-all' }}>
                            POST /api/1c/v3/{configuringSupplier.id}/catalog?type=catalog&amp;mode=file&amp;filename=goods_1_1_xxx.xml
                          </code>
                          <p style={{ marginTop: '8px', fontSize: '12px', color: '#6b7280' }}>
                            Укажите этот URL в настройках выгрузки 1С (CommerceML 3.1). Используйте логин и пароль поставщика для авторизации.
                          </p>
                        </div>

                        <div className="form-group">
                          <label>Название свойства для наименования:</label>
                          <input
                            type="text"
                            className="input"
                            value={configFormData.name_property_name}
                            onChange={(e) => setConfigFormData({...configFormData, name_property_name: e.target.value})}
                            placeholder="Полное наименование"
                          />
                          <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                            Название реквизита для полного наименования из ЗначенияРеквизитов (например, "Полное наименование")
                          </small>
                        </div>

                        <div className="form-group">
                          <label>Тег цены в prices_*.xml:</label>
                          <input
                            type="text"
                            className="input"
                            value={configFormData.xml_offers_price_tag || ''}
                            onChange={(e) => setConfigFormData({...configFormData, xml_offers_price_tag: e.target.value || null})}
                            placeholder="ЦенаЗаЕдиницу"
                          />
                          <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                            Название XML-тега с ценой внутри &lt;Цена&gt; (по умолчанию: ЦенаЗаЕдиницу)
                          </small>
                        </div>

                        <div className="form-group">
                          <label>Тег валюты в prices_*.xml:</label>
                          <input
                            type="text"
                            className="input"
                            value={configFormData.xml_offers_currency_tag || ''}
                            onChange={(e) => setConfigFormData({...configFormData, xml_offers_currency_tag: e.target.value || null})}
                            placeholder="Валюта"
                          />
                          <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                            Название XML-тега с валютой внутри &lt;Цена&gt; (по умолчанию: Валюта)
                          </small>
                        </div>
                      </>
                    )}
                  </>
                )}

                {/* Конфигурация для XML 1C v2 (CommerceML 2.x) */}
                {configuringSupplier.update_method === 'xml_1c_v2' && (
                  <>
                    <div style={{ 
                      background: '#f0fdf4', 
                      padding: '12px', 
                      borderRadius: '6px',
                      marginBottom: '20px',
                      fontSize: '14px',
                      color: '#065f46',
                      borderLeft: '4px solid #16a34a'
                    }}>
                      <strong>XML 1C v2 (CommerceML 2.x):</strong> Файлы загружаются вручную в директорию
                      <code>/app/uploads/1c_v2/{configuringSupplier.id}/</code>.
                      Поддерживаются файлы: <code>import___*.xml</code>, <code>rests___*.xml</code>, <code>prices___*.xml</code>.
                    </div>

                    <div className="form-group">
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input
                          type="checkbox"
                          checked={configFormData.xml_1c_v2_enabled}
                          onChange={(e) => setConfigFormData({...configFormData, xml_1c_v2_enabled: e.target.checked})}
                          style={{ width: '18px', height: '18px' }}
                        />
                        <span>Включить импорт XML 1C v2</span>
                      </label>
                    </div>

                    {configFormData.xml_1c_v2_enabled && (
                      <>
                        <div className="form-group">
                          <label>Тег цены в prices___*.xml:</label>
                          <input
                            type="text"
                            className="input"
                            value={configFormData.xml_offers_price_tag || ''}
                            onChange={(e) => setConfigFormData({...configFormData, xml_offers_price_tag: e.target.value || null})}
                            placeholder="ЦенаЗаЕдиницу"
                          />
                        </div>
                        <div className="form-group">
                          <label>Тег валюты в prices___*.xml:</label>
                          <input
                            type="text"
                            className="input"
                            value={configFormData.xml_offers_currency_tag || ''}
                            onChange={(e) => setConfigFormData({...configFormData, xml_offers_currency_tag: e.target.value || null})}
                            placeholder="Валюта"
                          />
                        </div>
                      </>
                    )}
                  </>
                )}

                {/* Конфигурация для простого XML */}
                {configuringSupplier.update_method === 'xml' && (
                  <>
                    <div style={{ 
                      background: '#f3f4f6', 
                      padding: '12px', 
                      borderRadius: '6px',
                      marginBottom: '20px',
                      fontSize: '14px',
                      color: '#374151'
                    }}>
                      <strong>Пример:</strong> Укажите URL XML файла и названия тегов для артикула, бренда, остатка и цены.
                      Например, для Sirpi: Артикул, Бренд, Количество, РРЦ.
                    </div>

                    <div className="form-group">
                      <label>URL XML файла для автоматического скачивания:</label>
                      <input
                        type="url"
                        className="input"
                        value={configFormData.xml_url}
                        onChange={(e) => setConfigFormData({...configFormData, xml_url: e.target.value})}
                        placeholder="https://example.com/products.xml"
                        required
                      />
                    </div>

                    <div style={{ 
                      background: '#f9fafb', 
                      padding: '12px', 
                      borderRadius: '6px',
                      marginTop: '20px',
                      marginBottom: '12px'
                    }}>
                      <h3 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>Настройка тегов XML</h3>
                    </div>

                    <div className="form-group">
                      <label>Тег артикула:</label>
                      <input
                        type="text"
                        className="input"
                        value={configFormData.xml_tag_sku}
                        onChange={(e) => setConfigFormData({...configFormData, xml_tag_sku: e.target.value})}
                        placeholder="Артикул"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>Тег бренда:</label>
                      <input
                        type="text"
                        className="input"
                        value={configFormData.xml_tag_brand}
                        onChange={(e) => setConfigFormData({...configFormData, xml_tag_brand: e.target.value})}
                        placeholder="Бренд"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>Тег остатка:</label>
                      <input
                        type="text"
                        className="input"
                        value={configFormData.xml_tag_stock}
                        onChange={(e) => setConfigFormData({...configFormData, xml_tag_stock: e.target.value})}
                        placeholder="Количество"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>Тег цены (опционально):</label>
                      <input
                        type="text"
                        className="input"
                        value={configFormData.xml_tag_price}
                        onChange={(e) => setConfigFormData({...configFormData, xml_tag_price: e.target.value})}
                        placeholder="РРЦ"
                      />
                    </div>

                    <div className="form-group">
                      <label>Тег валюты (опционально):</label>
                      <input
                        type="text"
                        className="input"
                        value={configFormData.xml_tag_currency || ''}
                        onChange={(e) => setConfigFormData({...configFormData, xml_tag_currency: e.target.value || null})}
                        placeholder="Например: Валюта"
                      />
                      <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                        Тег с кодом валюты цены (EUR, USD, GBP). Если не указан — цена считается в рублях.
                      </small>
                    </div>

                    <div style={{ 
                      background: '#f9fafb', 
                      padding: '12px', 
                      borderRadius: '6px',
                      marginTop: '20px',
                      marginBottom: '12px'
                    }}>
                      <h3 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>Дополнительные настройки обработки</h3>
                    </div>

                    <div className="form-group">
                      <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={configFormData.xml_reverse_sku_parts || false}
                          onChange={(e) => setConfigFormData({...configFormData, xml_reverse_sku_parts: e.target.checked})}
                          style={{ marginRight: '8px' }}
                        />
                        Реверсировать части артикула
                      </label>
                      <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                        Искать артикул в обоих вариантах: "21210 OP" → "21210OP" и "OP21210"
                      </small>
                    </div>

                    <div className="form-group">
                      <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={configFormData.xml_strip_price_code || false}
                          onChange={(e) => setConfigFormData({...configFormData, xml_strip_price_code: e.target.checked})}
                          style={{ marginRight: '8px' }}
                        />
                        Отбрасывать прайс-код
                      </label>
                      <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                        Убирать символы A-E в конце артикула через пробел: "1122 MK B" → "1122 MK"
                      </small>
                    </div>

                    <div className="form-group">
                      <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={configFormData.xml_digits_only_sku || false}
                          onChange={(e) => setConfigFormData({...configFormData, xml_digits_only_sku: e.target.checked})}
                          style={{ marginRight: '8px' }}
                        />
                        Отбрасывать буквы из артикула
                      </label>
                      <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                        Сравнивать только цифры: "500044" совпадёт с "KA500044". Работает если в артикуле не менее 5 цифр подряд.
                      </small>
                    </div>

                    <div className="form-group">
                      <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={configFormData.xml_ignore_brand || false}
                          onChange={(e) => setConfigFormData({...configFormData, xml_ignore_brand: e.target.checked})}
                          style={{ marginRight: '8px' }}
                        />
                        Не использовать бренд из файла
                      </label>
                      <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                        Искать только по артикулу среди брендов поставщика. Включить если в XML нет тега бренда.
                      </small>
                    </div>

                    <div className="form-group">
                      <label>Маска артикула (regex):</label>
                      <input
                        type="text"
                        className="input"
                        value={configFormData.xml_sku_mask || ''}
                        onChange={(e) => setConfigFormData({...configFormData, xml_sku_mask: e.target.value})}
                        placeholder="Например: ^(\S+)\s"
                      />
                      <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                        Если нужно извлечь часть из тега. Берётся группа 1. Строка пропускается если маска не совпала.<br/>
                        Пример: <code>^(\S+)\s</code> из "100/10049 Обои Cole & Son" → "100/10049"
                      </small>
                    </div>

                    <div className="form-group">
                      <label>Маска бренда (regex):</label>
                      <input
                        type="text"
                        className="input"
                        value={configFormData.xml_brand_mask || ''}
                        onChange={(e) => setConfigFormData({...configFormData, xml_brand_mask: e.target.value})}
                        placeholder="Например: ^\S+\s+(.+)$"
                      />
                      <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                        Regex-маска для извлечения бренда из тега. Берётся группа 1.<br/>
                        Пример: <code>^\S+\s+(.+)$</code> из "100/10049 Cole & Son" → "Cole & Son"
                      </small>
                    </div>
                  </>
                )}

                {/* Конфигурация для Excel */}
                {configuringSupplier.update_method === 'xls' && (
                  <>
                    <div style={{ 
                      background: '#f3f4f6', 
                      padding: '12px', 
                      borderRadius: '6px',
                      marginBottom: '20px',
                      fontSize: '14px',
                      color: '#374151'
                    }}>
                      <strong>Пример:</strong> Если артикул находится в колонке B, остаток в C - укажите соответствующие буквы колонок.
                      Номер строки заголовка - это строка с названиями колонок в Excel.
                    </div>


                <div className="form-group">
                  <label>URL файла для автоматического скачивания:</label>
                  <input
                    type="text"
                    className="input"
                    value={configFormData.file_url}
                    onChange={(e) => setConfigFormData({...configFormData, file_url: e.target.value})}
                    placeholder="https://example.com/file.xls или ссылка на Google Sheets"
                  />
                  <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                    Поддерживаются прямые ссылки на XLS/XLSX/CSV файлы, а также ссылки на Google Sheets (например, <code>https://docs.google.com/spreadsheets/d/.../edit?gid=...</code>) — они автоматически конвертируются в ссылку для скачивания.
                    Оставьте пустым для поставщиков с галкой «Загружаем сами».
                  </small>
                </div>

                <div className="form-group">
                  <label>URL второго файла (опционально):</label>
                  <input
                    type="text"
                    className="input"
                    value={configFormData.file_url_2}
                    onChange={(e) => setConfigFormData({...configFormData, file_url_2: e.target.value})}
                    placeholder="https://example.com/file2.xls или ссылка на Google Sheets"
                  />
                  <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                    Если поставщик даёт два прайс-файла — укажите второй здесь. Поддерживаются прямые ссылки и Google Sheets. Оба файла будут обработаны при каждом обновлении.
                  </small>
                </div>

                {/* Секция авторизации */}
                <div style={{ 
                  background: '#fef3c7', 
                  padding: '16px', 
                  borderRadius: '8px',
                  marginBottom: '20px',
                  border: '1px solid #fbbf24'
                }}>
                  <div className="form-group" style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontWeight: '600' }}>
                      <input
                        type="checkbox"
                        checked={configFormData.auth_enabled}
                        onChange={(e) => setConfigFormData({...configFormData, auth_enabled: e.target.checked})}
                        style={{ marginRight: '8px' }}
                      />
                      🔐 Требуется авторизация
                    </label>
                    <small style={{ color: '#78350f', fontSize: '12px', marginTop: '4px', display: 'block', marginLeft: '24px' }}>
                      Включите, если для скачивания файла нужен логин и пароль
                    </small>
                  </div>

                  {configFormData.auth_enabled && (
                    <>
                      <div className="form-group">
                        <label>Тип авторизации:</label>
                        <select
                          className="input"
                          value={configFormData.auth_type}
                          onChange={(e) => setConfigFormData({...configFormData, auth_type: e.target.value})}
                        >
                          <option value="form">Форма авторизации (сайт с логином)</option>
                          <option value="basic">Basic Auth (HTTP заголовок)</option>
                          <option value="bearer">Bearer Token (API)</option>
                          <option value="digest">Digest Auth</option>
                          <option value="ftp">FTP (File Transfer Protocol)</option>
                        </select>
                        <small style={{ color: '#78350f', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                          💡 Для сайтов с формой входа выберите "Форма авторизации"
                        </small>
                      </div>

                      {configFormData.auth_type === 'bearer' ? (
                        <div className="form-group">
                          <label>Bearer Token:</label>
                          <input
                            type="password"
                            className="input"
                            value={configFormData.auth_password}
                            onChange={(e) => setConfigFormData({...configFormData, auth_password: e.target.value})}
                            placeholder="Вставьте токен авторизации"
                          />
                        </div>
                      ) : (
                        <>
                          <div className="form-group">
                            <label>Логин:</label>
                            <input
                              type="text"
                              className="input"
                              value={configFormData.auth_username}
                              onChange={(e) => setConfigFormData({...configFormData, auth_username: e.target.value})}
                              placeholder="username"
                              autoComplete="username"
                            />
                          </div>

                          <div className="form-group">
                            <label>Пароль:</label>
                            <input
                              type="password"
                              className="input"
                              value={configFormData.auth_password}
                              onChange={(e) => setConfigFormData({...configFormData, auth_password: e.target.value})}
                              placeholder="••••••••"
                              autoComplete="current-password"
                            />
                            <small style={{ color: '#78350f', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                              ⚠️ Пароль хранится в базе данных. Используйте отдельный аккаунт для API.
                            </small>
                          </div>

                          {configFormData.auth_type === 'form' && (
                            <>
                              <div style={{ 
                                background: '#dbeafe', 
                                padding: '12px', 
                                borderRadius: '6px',
                                marginTop: '16px',
                                marginBottom: '12px',
                                border: '1px solid #60a5fa'
                              }}>
                                <div style={{ fontWeight: '600', marginBottom: '8px', color: '#1e40af' }}>
                                  🔧 Расширенные настройки (опционально)
                                </div>
                                <div style={{ fontSize: '12px', color: '#1e3a8a' }}>
                                  Если автоматическая авторизация не работает, укажите точный URL страницы входа и названия полей формы.
                                  Эти данные можно найти в HTML коде страницы авторизации.
                                </div>
                              </div>

                              <div className="form-group">
                                <label>URL страницы авторизации (опционально):</label>
                                <input
                                  type="url"
                                  className="input"
                                  value={configFormData.auth_login_url || ''}
                                  onChange={(e) => setConfigFormData({...configFormData, auth_login_url: e.target.value})}
                                  placeholder="https://opt.masterdom.ru/login"
                                />
                                <small style={{ color: '#78350f', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                                  Точный URL страницы, на которую отправляется форма входа
                                </small>
                              </div>

                              <div className="form-group">
                                <label>Имя поля логина (опционально):</label>
                                <input
                                  type="text"
                                  className="input"
                                  value={configFormData.auth_username_field || ''}
                                  onChange={(e) => setConfigFormData({...configFormData, auth_username_field: e.target.value})}
                                  placeholder="username, login, email"
                                />
                                <small style={{ color: '#78350f', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                                  Атрибут "name" поля логина в HTML форме (например: username, login, email)
                                </small>
                              </div>

                              <div className="form-group">
                                <label>Имя поля пароля (опционально):</label>
                                <input
                                  type="text"
                                  className="input"
                                  value={configFormData.auth_password_field || ''}
                                  onChange={(e) => setConfigFormData({...configFormData, auth_password_field: e.target.value})}
                                  placeholder="password, pass, passwd"
                                />
                                <small style={{ color: '#78350f', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                                  Атрибут "name" поля пароля в HTML форме (например: password, pass, passwd)
                                </small>
                              </div>
                            </>
                          )}
                          
                          {configFormData.auth_type === 'ftp' && (
                            <>
                              <div style={{ 
                                background: '#dbeafe', 
                                padding: '12px', 
                                borderRadius: '6px',
                                marginTop: '16px',
                                marginBottom: '12px',
                                border: '1px solid #60a5fa'
                              }}>
                                <div style={{ fontWeight: '600', marginBottom: '8px', color: '#1e40af' }}>
                                  📡 Настройки FTP
                                </div>
                                <div style={{ fontSize: '12px', color: '#1e3a8a' }}>
                                  Укажите FTP сервер, путь к файлу и данные для авторизации
                                </div>
                              </div>

                              <div className="form-group">
                                <label>FTP Хост:</label>
                                <input
                                  type="text"
                                  className="input"
                                  value={configFormData.ftp_host || ''}
                                  onChange={(e) => setConfigFormData({...configFormData, ftp_host: e.target.value})}
                                  placeholder="ftp.example.com"
                                  required
                                />
                                <small style={{ color: '#78350f', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                                  Адрес FTP сервера (например: ftp.selcdn.ru)
                                </small>
                              </div>

                              <div className="form-group">
                                <label>FTP Порт:</label>
                                <input
                                  type="number"
                                  className="input"
                                  value={configFormData.ftp_port || 21}
                                  onChange={(e) => setConfigFormData({...configFormData, ftp_port: parseInt(e.target.value)})}
                                  placeholder="21"
                                  min="1"
                                  max="65535"
                                />
                                <small style={{ color: '#78350f', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                                  Обычно 21 для FTP
                                </small>
                              </div>

                              <div className="form-group">
                                <label>Путь к директории:</label>
                                <input
                                  type="text"
                                  className="input"
                                  value={configFormData.ftp_path || ''}
                                  onChange={(e) => setConfigFormData({...configFormData, ftp_path: e.target.value})}
                                  placeholder="/directory/"
                                  required
                                />
                                <small style={{ color: '#78350f', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                                  Путь к директории на FTP (например: /ExchangeSKU/)
                                </small>
                              </div>

                              <div className="form-group">
                                <label>Regex паттерн для поиска файла:</label>
                                <input
                                  type="text"
                                  className="input"
                                  value={configFormData.ftp_file_pattern || ''}
                                  onChange={(e) => setConfigFormData({...configFormData, ftp_file_pattern: e.target.value})}
                                  placeholder=".*остатки.*\.xls$ или оставьте пустым"
                                />
                                <small style={{ color: '#78350f', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                                  Опционально: если несколько файлов, используйте regex для фильтрации. Будет выбран самый новый подходящий файл.
                                </small>
                              </div>

                              <div className="form-group">
                                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                  <input
                                    type="checkbox"
                                    checked={configFormData.ftp_passive}
                                    onChange={(e) => setConfigFormData({...configFormData, ftp_passive: e.target.checked})}
                                    style={{ marginRight: '8px' }}
                                  />
                                  Использовать пассивный режим (рекомендуется)
                                </label>
                                <small style={{ color: '#78350f', fontSize: '12px', marginTop: '4px', display: 'block', marginLeft: '24px' }}>
                                  Пассивный режим работает лучше с большинством современных FTP серверов
                                </small>
                              </div>
                            </>
                          )}
                        </>
                      )}
                    </>
                  )}
                </div>

                <div className="form-group">
                  <label>Колонка артикула (SKU):</label>
                  <input
                    type="text"
                    className="input"
                    value={configFormData.column_sku}
                    onChange={(e) => setConfigFormData({...configFormData, column_sku: e.target.value.toUpperCase()})}
                    placeholder="B"
                    maxLength="2"
                    required
                    style={{ textTransform: 'uppercase' }}
                  />
                </div>

                <div className="form-group">
                  <label>Колонка бренда:</label>
                  <input
                    type="text"
                    className="input"
                    value={configFormData.column_brand}
                    onChange={(e) => setConfigFormData({...configFormData, column_brand: e.target.value.toUpperCase()})}
                    placeholder="B"
                    maxLength="2"
                    style={{ textTransform: 'uppercase' }}
                  />
                </div>

                <div className="form-group">
                  <label>Колонка остатка (Stock):</label>
                  <input
                    type="text"
                    className="input"
                    value={configFormData.column_stock}
                    onChange={(e) => setConfigFormData({...configFormData, column_stock: e.target.value.toUpperCase()})}
                    placeholder="C"
                    maxLength="2"
                    required
                    style={{ textTransform: 'uppercase' }}
                  />
                </div>

                <div className="form-group">
                  <label>Номер строки заголовка:</label>
                  <input
                    type="number"
                    className="input"
                    value={configFormData.header_row}
                    onChange={(e) => setConfigFormData({...configFormData, header_row: parseInt(e.target.value)})}
                    placeholder="8"
                    min="1"
                    required
                  />
                  <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                    Строка, где находятся названия колонок (обычно 1 или 8)
                  </div>
                </div>

                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={configFormData.reverse_sku_parts || false}
                      onChange={(e) => setConfigFormData({...configFormData, reverse_sku_parts: e.target.checked})}
                      style={{ marginRight: '8px' }}
                    />
                    Реверсировать части артикула
                  </label>
                  <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                    Искать артикул в обоих вариантах: "21210 OP" → "21210OP" и "OP21210"
                  </small>
                </div>

                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={configFormData.strip_price_code || false}
                      onChange={(e) => setConfigFormData({...configFormData, strip_price_code: e.target.checked})}
                      style={{ marginRight: '8px' }}
                    />
                    Отбрасывать прайс-код
                  </label>
                  <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                    Убирать символы A-E в конце артикула через пробел: "1122 MK B" → "1122 MK"
                  </small>
                </div>

                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={configFormData.digits_only_sku || false}
                      onChange={(e) => setConfigFormData({...configFormData, digits_only_sku: e.target.checked})}
                      style={{ marginRight: '8px' }}
                    />
                    Отбрасывать буквы из артикула
                  </label>
                  <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                    Сравнивать только цифры: "500044" совпадёт с "KA500044". Работает если в артикуле не менее 5 цифр подряд.
                  </small>
                </div>

                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={configFormData.xls_ignore_brand || false}
                      onChange={(e) => setConfigFormData({...configFormData, xls_ignore_brand: e.target.checked})}
                      style={{ marginRight: '8px' }}
                    />
                    Не использовать бренд из файла
                  </label>
                  <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                    Искать только по артикулу среди брендов поставщика. Включить если в файле нет колонки бренда.
                  </small>
                </div>

                <div className="form-group">
                  <label>Маска артикула (regex):</label>
                  <input
                    type="text"
                    className="input"
                    value={configFormData.xls_sku_mask || ''}
                    onChange={(e) => setConfigFormData({...configFormData, xls_sku_mask: e.target.value})}
                    placeholder="Например: ^(\S+)\s"
                  />
                  <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                    Если артикул и бренд в одной колонке. Берётся группа 1. Строка пропускается если маска не совпала.<br/>
                    Пример: <code>^(\S+)\s</code> из "100/10049 Обои Cole &amp; Son" → "100/10049"
                  </small>
                </div>

                <div className="form-group">
                  <label>Маска бренда (regex):</label>
                  <input
                    type="text"
                    className="input"
                    value={configFormData.xls_brand_mask || ''}
                    onChange={(e) => setConfigFormData({...configFormData, xls_brand_mask: e.target.value})}
                    placeholder="Например: ^\S+\s+(.+)$"
                  />
                  <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                    Regex-маска для извлечения бренда из колонки. Берётся группа 1.<br/>
                    Пример: <code>^\S+\s+(.+)$</code> из "100/10049 Cole &amp; Son" → "Cole &amp; Son"
                  </small>
                </div>
                  </>
                )}
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeConfigModal}>
                  Отмена
                </button>
                <button type="submit" className="btn btn-primary">
                  Сохранить конфигурацию
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Модальное окно результатов загрузки маппинга */}
      {mappingUploadResult && (
        <div className="modal-overlay" style={{ zIndex: 1100 }} onClick={() => setMappingUploadResult(null)}>
          <div
            className="modal"
            style={{ maxWidth: '560px', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}
            onClick={e => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2 style={{ fontSize: '16px', fontWeight: '600' }}>Результат загрузки маппинга</h2>
              <button className="modal-close" onClick={() => setMappingUploadResult(null)}>×</button>
            </div>
            <div className="modal-body" style={{ overflowY: 'auto', flex: 1 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '16px' }}>
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '12px', textAlign: 'center' }}>
                  <div style={{ fontSize: '28px', fontWeight: '700', color: '#16a34a' }}>{mappingUploadResult.added}</div>
                  <div style={{ fontSize: '12px', color: '#15803d' }}>Загружено</div>
                </div>
                <div style={{ background: mappingUploadResult.skipped_not_found_count > 0 ? '#fff7ed' : '#f9fafb', border: `1px solid ${mappingUploadResult.skipped_not_found_count > 0 ? '#fed7aa' : '#e5e7eb'}`, borderRadius: '8px', padding: '12px', textAlign: 'center' }}>
                  <div style={{ fontSize: '28px', fontWeight: '700', color: mappingUploadResult.skipped_not_found_count > 0 ? '#ea580c' : '#6b7280' }}>{mappingUploadResult.skipped_not_found_count}</div>
                  <div style={{ fontSize: '12px', color: mappingUploadResult.skipped_not_found_count > 0 ? '#c2410c' : '#6b7280' }}>Не найдено</div>
                </div>
              </div>
              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>
                Всего строк в файле: <strong>{mappingUploadResult.total_rows}</strong>
                {mappingUploadResult.skipped_duplicate_count > 0 && (
                  <span style={{ marginLeft: '12px' }}>Дублей пропущено: <strong>{mappingUploadResult.skipped_duplicate_count}</strong></span>
                )}
              </div>

              {mappingUploadResult.skipped_not_found?.length > 0 && (
                <>
                  <div style={{ fontWeight: '600', fontSize: '13px', color: '#374151', marginBottom: '6px' }}>
                    Артикулы не найдены в базе ({mappingUploadResult.skipped_not_found.length}):
                  </div>
                  <div style={{
                    background: '#fafafa', border: '1px solid #e5e7eb', borderRadius: '6px',
                    maxHeight: '280px', overflowY: 'auto', fontFamily: 'monospace', fontSize: '12px',
                    padding: '8px 10px', lineHeight: '1.8'
                  }}>
                    {mappingUploadResult.skipped_not_found.map((sku, i) => (
                      <div key={i} style={{ color: '#b45309', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ color: '#d1d5db', minWidth: '28px', textAlign: 'right' }}>{i + 1}.</span>
                        {sku}
                      </div>
                    ))}
                  </div>
                </>
              )}

              {mappingUploadResult.skipped_not_found_count === 0 && (
                <div style={{ color: '#16a34a', fontSize: '13px', textAlign: 'center', padding: '8px' }}>
                  ✓ Все артикулы успешно сопоставлены
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setMappingUploadResult(null)}
              >
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuppliersPage;
